import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./CustomizableForm.module.css";
import { isNonEmptyArray, isNonEmptyObject } from "../../utils";
import Section from "./section/Section";

const toggleOpenById = (arr, uuid) => {
	for (const item of arr) {
		if (item.uuid === uuid || item.id === uuid) {
			if (item?.open === undefined) {
				item.open = false;
			} else {
				item.open = !item.open;
			}
			return;
		}
		if (item?.subsection) {
			toggleOpenById(item.subsection, uuid);
		}
	}
};

const BREAKPOINTS = ["lg", "md", "sm", "xs", "xxs"];

export const SectionsContext = createContext();

export default function CustomizableForm({
	className = "",
	customPageId,
	editMode = false,
	hasEditorRights,
	projectId,
	sections = {},
	onGlobalUpdate = null, // if null then update is on the fly
}) {
	const [localSections, setLocalSection] = useState({});
	useEffect(() => {
		setLocalSection({
			...sections,
			sections: (sections?.sections || []).map((sct) => ({
				...sct,
				open: (sct?.layout || []).some((block) => block?.options?.isMandatory) || sct?.defaultOpen,
				/** section is openable if it has a mandatory block or has default open variable true */
			})),
		});
	}, [sections]);
	const layoutIndex = useMemo(() => {
		const index = {};
		const indexSections = (sects) => {
			sects.forEach((section) => {
				if (isNonEmptyArray(section.layout)) {
					section.layout.forEach((item) => {
						index[item.id] = { value: undefined, name: "" };
						index[item.id].value = item.options.currentValue || item.options.currentValueIds;
						index[item.id].name = item.name;
					});
				}
				if (isNonEmptyArray(section?.subsections)) {
					indexSections(section.subsections);
				}
			});
		};
		if (isNonEmptyArray(localSections?.sections)) {
			indexSections(localSections.sections);
		}
		return index;
	}, [localSections]);
	const handleCollapseSection = (uuid) => {
		setLocalSection((prev) => {
			const newLocalSections = { ...prev };
			toggleOpenById(newLocalSections?.sections, uuid);
			return newLocalSections;
		});
	};
	const checkBreakpoint = (breakpoint) => (BREAKPOINTS.some((bp) => bp === breakpoint) && breakpoint) || "lg";
	const updateLocalSection = useCallback((sectionId, layoutId, newValue) => {
		const updateSections = (sects) =>
			sects.map((section) => {
				if (section.id === sectionId && isNonEmptyArray(section?.layout)) {
					return {
						...section,
						layout: section.layout.map((item) =>
							item.id !== layoutId
								? item
								: {
										...item,
										options: { ...item.options, ...newValue },
								  }
						),
					};
				}
				if (isNonEmptyArray(section?.subsections)) {
					return { ...section, subsections: updateSections(section.subsections) };
				}
				return section;
			});
		setLocalSection((prev) => ({ ...prev, sections: updateSections(prev.sections) }));
	}, []);
	return (
		<SectionsContext.Provider value={{ layoutIndex, updateLocalSection }}>
			<div className={`${styles.formContainer} ${className}`}>
				{isNonEmptyObject(localSections) &&
					isNonEmptyArray(localSections?.sections) &&
					localSections.sections.map((sct) => (
						<Section
							key={sct.id}
							breakpoint={checkBreakpoint(sections?.breakpoint)}
							customPageId={customPageId}
							editMode={editMode}
							hasEditorRights={hasEditorRights}
							projectId={projectId}
							section={sct}
							onCollapseSection={handleCollapseSection}
							onGlobalUpdate={onGlobalUpdate}
						/>
					))}
			</div>
		</SectionsContext.Provider>
	);
}
